import React, { useState, useEffect } from 'react';
import { collection, addDoc, deleteDoc, doc, setDoc, serverTimestamp, orderBy, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import ImageUploader from '../components/ImageUploader';
import { useAuth } from '../AuthContext';
import { Link } from 'react-router-dom';
import './ExperiencesPage.css';
import LoadingSpinner from '../components/LoadingSpinner';
import EditableContentMultilang from '../components/EditableContentMultilang';
import { useTranslation } from 'react-i18next';

// --- Componente per Input Multilingua ---
const MultilangInput = ({ label, field, values, onChange, isTextarea = false, rows = 3 }) => {
    const languages = ['it', 'en', 'de', 'nl', 'fr'];
    return (
        <div className="form-group">
            <label>{label}</label>
            <div className="multilang-input-grid">
                {languages.map(lang => ( isTextarea ? ( <textarea key={lang} value={values[lang] || ''} onChange={(e) => onChange(field, lang, e.target.value)} placeholder={lang.toUpperCase()} rows={rows} className="multilang-input"/> ) : ( <input key={lang} type="text" value={values[lang] || ''} onChange={(e) => onChange(field, lang, e.target.value)} placeholder={lang.toUpperCase()} className="multilang-input"/> ) ))}
            </div>
        </div>
    );
};

// --- Componente per il selettore di immagini ---
const ImageSelectorModal = ({ uploadedImages, onSelect, onCancel }) => {
    return (
        <div className="image-selector-modal-overlay" onClick={onCancel}>
            <div className="image-selector-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Scegli un'immagine caricata</h3>
                {uploadedImages.length === 0 ? ( <p>No pre-loaded images.</p> ) : (
                    <div className="image-selector-grid">
                        {uploadedImages.map((url, index) => ( <div key={index} className="image-selector-item" onClick={() => onSelect(url)}><img src={url} alt={`Immagine caricata ${index + 1}`} /></div> ))}
                    </div>
                )}
                <button type="button" className="cancel-button" onClick={onCancel}>Close</button>
            </div>
        </div>
    );
};

// --- Componente per gestire un singolo blocco di contenuto ---
const ContentBlockEditor = ({ block, index, onUpdate, onRemove, onMove, onOpenImageSelector }) => {
    const renderSizeSelector = () => {
        const options = block.type === 'text' ? [ { value: 'normal', label: 'Normale' }, { value: 'large', label: 'Grande' }, { value: 'medium', label: 'Medio' }, { value: 'small', label: 'Piccolo' } ] : [ { value: 'full', label: 'Piena Larghezza' }, { value: 'large', label: 'Grande (75%)' }, { value: 'medium', label: 'Media (50%)' }, { value: 'small', label: 'Piccola (30%)' } ];
        return ( <select className="block-size-selector" value={block.size || (block.type === 'text' ? 'normal' : 'full')} onChange={(e) => onUpdate(index, 'size', e.target.value)} title="Dimensione Blocco">{options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select> );
    };
    const handleTextBlockChange = (field, lang, value) => { const newTextValue = { ...(block.value || {}), [lang]: value }; onUpdate(index, 'value', newTextValue); };
    if (block.type === 'text') { return ( <div className="content-block-editor"><div className="block-controls"><span>Blocco Testo</span>{renderSizeSelector()}<div className="block-actions"><button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}>▲</button><button type="button" onClick={() => onMove(index, 1)}>▼</button><button type="button" onClick={() => onRemove(index)} className="delete-block-btn">🗑️</button></div></div><MultilangInput label="" field="value" values={block.value || {}} onChange={handleTextBlockChange} isTextarea rows={6} /></div> ); }
    if (block.type === 'image') { return ( <div className="content-block-editor"><div className="block-controls"><span>Blocco Immagine</span>{renderSizeSelector()}</div><div className="image-block-input-group"><input type="text" value={block.value} onChange={(e) => onUpdate(index, 'value', e.target.value)} placeholder="URL immagine (incolla o scegli)" className="image-url-input"/><button type="button" onClick={() => onOpenImageSelector(index)} className="choose-image-btn">Scegli</button></div>{block.value && <img src={block.value} alt="anteprima" className="block-image-preview"/>}<div className="block-actions-bottom"><button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}>▲</button><button type="button" onClick={() => onMove(index, 1)}>▼</button><button type="button" onClick={() => onRemove(index)} className="delete-block-btn">🗑️</button></div></div> ); }
    return null;
};

// --- Componente Form per Esperienze ---
const ExperienceForm = ({ initialData = {}, onSubmit, onCancel, isSaving }) => {
    const [title, setTitle] = useState(initialData.title || {});
    const [description, setDescription] = useState(initialData.description || {});
    const [imageUrl, setImageUrl] = useState(initialData.imageUrl || '');
    const [order, setOrder] = useState(initialData.order || '');
    const [detailContent, setDetailContent] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [targetBlockIndex, setTargetBlockIndex] = useState(null);

    useEffect(() => {
        setTitle(initialData.title || {});
        setDescription(initialData.description || {});
        setImageUrl(initialData.imageUrl || '');
        setOrder(initialData.order || '');
        const contentWithIds = (initialData.detailContent || []).map(b => ({...b, _id: Math.random().toString(36).substr(2, 9)}));
        setDetailContent(contentWithIds);
        setUploadedImages([]);
    }, [initialData]);

    const handleTextChange = (field, lang, value) => { const setter = { title: setTitle, description: setDescription }[field]; if (setter) { setter(prev => ({ ...prev, [lang]: value })); } };
    const handleSubmit = (e) => { e.preventDefault(); const contentToSave = detailContent.map(({ _id, ...rest }) => rest); onSubmit({ title, description, imageUrl, order: order ? parseInt(order, 10) : 999, detailContent: contentToSave }); };
    const addContentBlock = (type) => setDetailContent([...detailContent, { type, value: type === 'text' ? {} : '', size: type === 'text' ? 'normal' : 'full', _id: Math.random().toString(36).substr(2, 9)}]);
    const updateContentBlock = (index, field, value) => { const nc = [...detailContent]; nc[index] = { ...nc[index], [field]: value }; setDetailContent(nc); };
    const removeContentBlock = (index) => { if (window.confirm("Rimuovere blocco?")) setDetailContent(detailContent.filter((_, i) => i !== index)); };
    const moveContentBlock = (index, dir) => { const ni = index + dir; if (ni < 0 || ni >= detailContent.length) return; const nc = [...detailContent]; [nc[index], nc[ni]] = [nc[ni], nc[index]]; setDetailContent(nc); };
    const openImageSelector = (index) => { setTargetBlockIndex(index); setIsSelectorOpen(true); };
    const handleImageSelect = (url) => { if (targetBlockIndex !== null) { updateContentBlock(targetBlockIndex, 'value', url); } setIsSelectorOpen(false); setTargetBlockIndex(null); };

    return (
        <>
            {isSelectorOpen && (<ImageSelectorModal uploadedImages={uploadedImages} onSelect={handleImageSelect} onCancel={() => setIsSelectorOpen(false)}/>)}
            <div className="experience-form-container">
                <form onSubmit={handleSubmit}>
                    <h4>{initialData.id ? 'Modifica Esperienza' : 'Aggiungi Nuova Esperienza'}</h4>
                    <h5>Informazioni Principali</h5>
                    <MultilangInput label="Titolo Esperienza*" field="title" values={title} onChange={handleTextChange} />
                    <MultilangInput label="Descrizione (Lista)*" field="description" values={description} onChange={handleTextChange} isTextarea />
                    <div className="form-group"><label>Ordine Visualizzazione</label><input type="number" value={order} onChange={(e) => setOrder(e.target.value)} disabled={isSaving}/></div>
                    <div className="form-group"><label>Immagine Principale*</label><ImageUploader currentImageUrl={imageUrl} onUploadComplete={setImageUrl} folderPath="experiences/main" /></div>
                    <hr className="form-divider" />
                    <h5>Contenuti Pagina Dettaglio</h5>
                    <div className="content-blocks-container">
                        {detailContent.map((block, index) => (<ContentBlockEditor key={block._id} index={index} block={block} onUpdate={updateContentBlock} onRemove={removeContentBlock} onMove={moveContentBlock} onOpenImageSelector={openImageSelector}/>))}
                    </div>
                    <div className="add-block-buttons">
                        <button type="button" onClick={() => addContentBlock('text')}>+ Aggiungi Testo</button>
                        <button type="button" onClick={() => addContentBlock('image')}>+ Aggiungi Immagine (Vuoto)</button>
                    </div>
                    <hr className="form-divider" />
                    <h5>Galleria Immagini (per questa esperienza)</h5>
                    <div className="temporary-image-gallery">
                        <ImageUploader currentImageUrl="" onUploadComplete={(url) => setUploadedImages(prev => [...prev, url])} folderPath="experiences/details_temp" />
                        <div className="uploaded-previews">{uploadedImages.map((url, i) => <img key={i} src={url} alt={`caricata ${i+1}`} />)}</div>
                    </div>
                    <div className="form-actions">
                        <button type="submit" className="cta-button" disabled={isSaving || !imageUrl}>{isSaving ? 'Salvataggio...' : 'Salva Esperienza'}</button>
                        <button type="button" className="cancel-button" onClick={onCancel} disabled={isSaving}>Annulla</button>
                    </div>
                </form>
            </div>
        </>
    );
};

const ExperienceCard = ({ experience, onEdit, onDelete }) => {
    const { i18n } = useTranslation();
    const { currentUser } = useAuth();
    const lang = i18n.language;
    const fallbackLang = 'it';
    const title = experience.title?.[lang] || experience.title?.[fallbackLang] || 'Titolo non disponibile';
    const description = experience.description?.[lang] || experience.description?.[fallbackLang] || 'Descrizione...';
    const { t } = useTranslation();
    return (
        <div className="card experience-item">
             <img src={experience.imageUrl || '/placeholder-image.jpg'} alt={title} className="card-img" onError={(e) => { e.target.onerror = null; e.target.src='/placeholder-image.jpg'}}/>
             <div className="card-body">
                 <h3>{title}</h3>
                 <p>{description}</p>
                 <Link to={`/esperienze/${experience.id}`} className="cta-button details-button">{t('buttonDiscoverMore')}</Link>
             </div>
              {currentUser && (
                <div className="admin-card-controls">
                     <button onClick={() => onEdit(experience)} title="Modifica Esperienza">✏️</button>
                     <button onClick={() => onDelete(experience.id, title)} title="Elimina Esperienza">🗑️</button>
                </div>
              )}
         </div>
    );
}

const ExperiencesPage = () => {
    const [experiences, setExperiences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { currentUser } = useAuth();
    const [showForm, setShowForm] = useState(false);
    const [editingExperience, setEditingExperience] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError(null);
        const q = query(collection(db, 'experiences'), orderBy('order', 'asc'), orderBy('title.it', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setExperiences(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (err) => {
            console.error("Errore caricamento esperienze:", err);
            setError("Impossibile caricare le esperienze.");
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleFormSubmit = async (formData) => {
        setIsSaving(true);
        setError(null);
        const dataToSave = { ...formData, lastUpdatedBy: currentUser?.email, updatedAt: serverTimestamp() };
        try {
            if (editingExperience?.id) {
                await setDoc(doc(db, 'experiences', editingExperience.id), dataToSave, { merge: true });
            } else {
                dataToSave.createdAt = serverTimestamp();
                await addDoc(collection(db, 'experiences'), dataToSave);
            }
            setShowForm(false);
            setEditingExperience(null);
        } catch (err) {
            setError("Errore durante il salvataggio.");
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleDelete = async (id, title) => {
        if (window.confirm(`Sei sicuro di voler eliminare l'esperienza "${title}"?`)) {
            try { await deleteDoc(doc(db, 'experiences', id)); alert(`Esperienza "${title}" eliminata.`); }
            catch (err) { alert("Errore durante l'eliminazione."); }
        }
    };
    
    const handleAddClick = () => { setEditingExperience(null); setShowForm(true); };
    const handleEditClick = (experience) => { setEditingExperience(experience); setShowForm(true); };
    const handleFormCancel = () => { setShowForm(false); setEditingExperience(null); };

    return (
                    <div className="experiences-page">
                <LoadingSpinner show={isSaving} />
                <section className="page-header">
                    <div className="container text-center">
                        <EditableContentMultilang docPath="experiences_page/header" field="title" elementType="h1" />
                        <EditableContentMultilang docPath="experiences_page/header" field="intro_text" elementType="p" />
                        {currentUser && !showForm && (<button onClick={handleAddClick} className="cta-button add-experience-button">+ Aggiungi Esperienza</button>)}
                    </div>
                </section>
                {showForm && currentUser && (
                    <section className="content-section add-experience-form-section">
                        <div className="container">
                            <ExperienceForm initialData={editingExperience || {}} onSubmit={handleFormSubmit} onCancel={handleFormCancel} isSaving={isSaving}/>
                            {error && !isSaving && <p className="form-status error">{error}</p>}
                        </div>
                    </section>
                )}
                <section className="content-section">
                    <div className="container">
                        {loading ? (
                            <p className="loading-text">Caricamento esperienze...</p>
                        ) : error ? (
                            <p className="form-status error">{error}</p>
                        ) : experiences.length === 0 ? (
                            <p className="no-content-text">Nessuna esperienza attualmente inserita.</p>
                        ) : (
                            <div className="experiences-list-grid">
                                {experiences.map(exp => (
                                    <ExperienceCard key={exp.id} experience={exp} onEdit={handleEditClick} onDelete={handleDelete}/>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </div>
    );
};

export default ExperiencesPage;

